import { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@emotion/react";
import MUIPopper from "../../../components/utilities/MUIPopper";
import { useContext } from "react";
import { ImageCacheContext } from "../../../global/ImageCacheContext";
import LazyLoad from "react-lazy-load";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const useStyles = makeStyles((theme) => ({
  text: {
    fontSize: "0.85em",
    marginBottom: "unset",
    "& .highlighted": {
      fontWeight: "600",
    },
  },
  wrapper: {
    "& .highlighted": {
      backgroundColor:
        theme?.palette?.highlight?.main || theme?.palette?.warning?.main,
      color:
        theme?.palette?.highlight?.contrastText ||
        theme?.palette?.warning?.contrastText,
    },
  },
  privateWrapper: {
    backgroundColor: theme?.palette?.error?.main
      ? alpha(theme?.palette?.error.main, 0.15)
      : null,
    "& .highlighted": {
      backgroundColor:
        theme?.palette?.highlight?.main || theme?.palette?.warning?.main,
      color:
        theme?.palette?.highlight?.contrastText ||
        theme?.palette?.warning?.contrastText,
    },
  },
}));

export function CardImagePreview(props) {
  const { card, large, superDense } = props;
  var imageCacheState = useContext(ImageCacheContext);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const go = async () => {
      if (!inView) return;
      setLoading(true);
      if (!card?.imageFileId) {
        setImage({
          url: `${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/assets/cardback.png`,
        });
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === card.imageFileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = card.imageFile?.id
          ? card.imageFile
          : card.image?.id
            ? card.image
            : await imageCacheState.fetchImage(card.imageFileId, "medium");
        if (file) {
          if (!file.url && file.cloudFrontDomain) {
            file.url = `${file.cloudFrontDomain}${
              superDense
                ? file.sizes?.small ||
                  file.sizes?.medium ||
                  file.sizes?.large ||
                  file.filename
                : file.filename
            }`;
          }

          setImage({
            id: card.imageFileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [inView]);

  return (
    <LazyLoad
      height={large ? 419 : superDense ? 40 : 307}
      onContentVisible={(e) => {
        setInView(true);
      }}
    >
      {!image?.url || loading ? (
        <Box
          sx={{
            flex: `0 0 ${large ? "300px" : superDense ? "28.57px" : "214px"}`,
            maxWidth: large ? "300px" : superDense ? "28.57px" : "214px",
            minWidth: large ? "300px" : superDense ? "28.57px" : "214px",
            height: large ? "419px" : superDense ? "40px" : "307px",
            mt: "-5px !important",
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading && <CircularProgress size={superDense ? 10 : 50} />}
        </Box>
      ) : (
        <img
          src={image.url}
          alt={card?.name}
          style={{
            flex: `0 0 ${large ? "300px" : superDense ? "28.57px" : "214px"}`,
            maxWidth: large ? "300px" : superDense ? "28.57px" : "214px",
            minWidth: large ? "300px" : superDense ? "28.57px" : "214px",
            minHeight: large ? "419px" : superDense ? "40px" : "300px",
            mt: "-5px !important",
            borderRadius: 2,
          }}
        />
      )}
    </LazyLoad>
  );
}

const interleave = (arr, thing) =>
  [].concat(...arr.map((n) => [n, thing])).slice(0, -1);

const replaceWithIcons = (textBox) => {
  var replaceItems = {};
  let text = textBox?.replace(/\\/g, "") || "";
  let replaceList = [
    { key: "M", fileName: "mars" },
    { key: "L", fileName: "luna" },
    { key: "S", fileName: "sol" },
    { key: "EX", fileName: "ex" },
    { key: "0", fileName: "0" },
    { key: "1", fileName: "1" },
    { key: "2", fileName: "2" },
    { key: "3", fileName: "3" },
    { key: "4", fileName: "4" },
    { key: "5", fileName: "5" },
    { key: "6", fileName: "6" },
    { key: "7", fileName: "7" },
    { key: "X", fileName: "x" },
  ];

  for (let rep of replaceList) {
    if (text.includes(`[${rep.key}]`)) {
      replaceItems[`[${rep.key}]`] = (
        <img
          src={`${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/assets/${rep.fileName}.png`}
          alt={rep.label || rep.key}
          style={{
            height: "18px",
            marginBottom: "-4px",
          }}
        />
      );
    }
    //  else if (text.includes(`${rep.key}`)) {
    //   replaceItems[`${rep.key}:`] = (
    //     <img
    //       src={`${getEnvironmentVariable(
    //         "SYSTEM_IMAGES_CLOUDFRONT"
    //       )}/images/assets/${rep.fileName}.png`}
    //       alt={rep.label || rep.key}
    //       style={{
    //         height: "18px",
    //         marginBottom: "-4px",
    //       }}
    //     />
    //   );
    // }
  }

  var match;
  let reEM = /<em>(.*?)<\/em>/g;
  while ((match = reEM.exec(text)) != null) {
    replaceItems[match[0]] = <span className="highlighted">{match[1]}</span>;
  }

  if (!Object.keys(replaceItems).length) {
    return text;
  }
  var newTextArray = [text];

  Object.keys(replaceItems).forEach((replaceItem) => {
    // loop through each replaceItem
    for (var i = 0; i < newTextArray.length; i++) {
      // loop through each already split item
      if (typeof newTextArray[i] === "string") {
        var item = newTextArray[i].split(replaceItem);
        if (item.length > 1) {
          item = interleave(item, replaceItems[replaceItem]);
          newTextArray.splice(i, 1, ...item);
        }
      }
    }
  });
  return newTextArray;
};

export function CardText(props) {
  const { card } = props;
  const [cardText, setCardText] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    let textArray = card?.textBox?.split("\n") || [];
    let newArray = [];
    if (card?.banned) {
      newArray.push({ heading: "Note: Card banned" });
    }
    let list = undefined;
    textArray.forEach((text) => {
      text = text.trim();
      if (text.startsWith("-")) {
        if (!list) {
          list = [];
        }
        list.push(text.substring(1).trim());
      } else {
        if (list?.length) {
          newArray.push({ list: list });
        }
        list = undefined;
        newArray.push({ text: text });
      }
    });
    if (list?.length) {
      newArray.push({ list: list });
    }
    setCardText(newArray);
  }, [card]);

  return (
    <Typography
      variant="body1"
      gutterBottom
      paragraph
      sx={{
        "& .highlighted": {
          fontWeight: "600",
          backgroundColor:
            theme?.palette?.highlight?.main || theme?.palette?.warning?.main,
          color:
            theme?.palette?.highlight?.contrastText ||
            theme?.palette?.warning?.contrastText,
        },
      }}
    >
      {cardText.map((i, key) => {
        if (i.list) {
          return (
            <List key={key} sx={{ listStyle: "disc" }}>
              {i.list.map((l, lKey) => (
                <ListItem
                  sx={{
                    pt: 0,
                    pb: 0,
                    display: "list-item",
                    listStylePosition: "inside",
                  }}
                  key={lKey}
                >
                  {replaceWithIcons(l)}
                </ListItem>
              ))}
            </List>
          );
        }
        return (
          <Box key={key} sx={{ mt: key === 0 ? null : 2 }}>
            {replaceWithIcons(i.text)}
          </Box>
        );
      })}
    </Typography>
  );
}

function ReturnComponent(props) {
  const {
    card,
    dense,
    superDense,
    leftClick,
    rightClick,
    buttons,
    hover,
    error,
    classes,
  } = props;

  if (dense) {
    return (
      <>
        {card && (
          <Link
            href={!leftClick && !hover ? `/card/${card.id}` : null}
            target={!leftClick && !hover ? "_blank" : null}
            onClick={
              leftClick
                ? (e) => {
                    leftClick(card);
                  }
                : null
            }
            onContextMenu={
              rightClick
                ? (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    rightClick(card);
                  }
                : null
            }
            color="inherit"
            underline="none"
            sx={{
              flexDirection: "row",
              boxSizing: "border-box",
              display: "flex",
              cursor: "pointer",
              mr: 1,
            }}
          >
            <Box sx={{ overflow: "hidden" }}>
              <CardImagePreview card={card} dense={dense} />
              {card && buttons && <>{buttons}</>}
            </Box>
          </Link>
        )}
      </>
    );
  }

  if (superDense) {
    return (
      <>
        <Box
          sx={{
            width: "100%",
            minHeight: "40px",
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 2,
            boxShadow: 1,
            backgroundColor: error ? "error.main" : null,
            color: error ? "error.contrastText" : null,
            "& .MuiButtonGroup-outlined": {
              backgroundColor: error ? "error.contrastText" : null,
            },
          }}
        >
          {card && (
            <Link
              href={
                !leftClick && !rightClick && !hover ? `/card/${card.id}` : null
              }
              target={!leftClick && !rightClick && !hover ? "_blank" : null}
              onClick={
                leftClick
                  ? (e) => {
                      leftClick(card);
                    }
                  : null
              }
              onContextMenu={
                rightClick
                  ? (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      rightClick(card);
                    }
                  : null
              }
              color="inherit"
              underline="none"
              sx={{
                flexDirection: "row",
                boxSizing: "border-box",
                display: "flex",
                cursor: "pointer",
              }}
            >
              <Grid container alignItems="center" sx={{ minHeight: "40px" }}>
                <Grid
                  item
                  sx={{ maxHeight: "40px", mr: 1, overflow: "hidden" }}
                >
                  <CardImagePreview card={card} superDense={superDense} />
                </Grid>
                <Grid item sx={{ flex: 1 }}>
                  <Typography gutterBottom className={classes.text}>
                    {card.name}
                  </Typography>
                </Grid>
                {card.qty !== undefined && (
                  <Grid item sx={{ mr: 1 }}>
                    <Chip label={`x${card.qty}`} size="small" color="success" />
                  </Grid>
                )}
                {buttons && (
                  <Grid item sx={{ mr: 1 }}>
                    {buttons}
                  </Grid>
                )}
              </Grid>
            </Link>
          )}
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: ["1 1 100%", "1 1 100%", "1 1 49%"], //, "1 1 33%"],
          boxSizing: "border-box",
          maxWidth: ["100%", "100%", "49%"], //, "33%"],

          mt: 1,
        }}
      >
        <Box
          className={
            card && !card.public ? classes.privateWrapper : classes.wrapper
          }
          sx={{
            flexFlow: "row wrap",
            boxSizing: "border-box",
            dispay: "flex",

            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 2,
            boxShadow: 1,
            backgroundColor: "background.secondary",

            borderStyle: card.banned ? "solid" : null,
            borderColor: card.banned ? "error.main" : null,
            borderWidth: card.banned ? "2px" : null,

            minWidth: "300px",
          }}
        >
          {card && (
            <Link
              href={
                !leftClick && !rightClick && !hover ? `/card/${card.id}` : null
              }
              target={!leftClick && !rightClick && !hover ? "_blank" : null}
              onClick={
                leftClick
                  ? (e) => {
                      leftClick(card);
                    }
                  : null
              }
              onContextMenu={
                rightClick
                  ? (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      rightClick(card);
                    }
                  : null
              }
              color="inherit"
              underline="none"
              sx={{
                flexDirection: "row",
                boxSizing: "border-box",
                display: "flex",
                cursor: "pointer",
                overflow: "hidden",
              }}
            >
              <CardImagePreview card={card} />

              <Box
                sx={{
                  flex: "1 1 0%",
                  boxSizing: "border-box",
                  ml: 1,
                  mr: "2px",
                  height: "300px",
                  maxHeight: "300px",
                  display: "flex",
                  flexFlow: "column",

                  background: card.banned
                    ? `linear-gradient(0deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6)), url('${getEnvironmentVariable(
                        "SYSTEM_IMAGES_CLOUDFRONT"
                      )}/images/assets/banned.png') center center/cover no-repeat`
                    : null,
                }}
              >
                <Box
                  sx={{
                    marginBottom: "5px",
                    flex: "0 1 auto",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-block",
                    }}
                  >
                    <Typography gutterBottom className={classes.text}>
                      {card.name}
                      {card.serial ? " (" + card.serial + ")" : ""}
                    </Typography>
                    <Typography
                      gutterBottom
                      variant="caption"
                      className={classes.text}
                    >
                      {card.cardType}
                      {card.cardSubType !== undefined
                        ? ` - ${card.cardSubType} `
                        : ""}
                      {card.cardSubTypeChild !== undefined
                        ? `${card.cardSubTypeChild} `
                        : ""}
                      - {card.rarity}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: "gray.main" }} />
                </Box>
                <Box
                  sx={{
                    flex: "1 1 auto",
                    overflow: "auto",
                    mb: "5px",
                  }}
                >
                  {card.keywords?.length > 0 && (
                    <Typography
                      gutterBottom
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66",
                      }}
                      textAlign="center"
                    >
                      {card.keywords.join(" - ")}
                    </Typography>
                  )}
                  {card.textBox &&
                    Array.isArray(card.textBox) &&
                    card.textBox.length > 0 && (
                      <Typography
                        gutterBottom
                        sx={{
                          fontWeight: "400",
                          fontSize: "0.75rem",
                          lineHeight: "1.66",
                        }}
                      >
                        {card.textBox}
                      </Typography>
                    )}
                  {card.textBox &&
                    !Array.isArray(card.textBox) &&
                    card.textBox !== "" && <CardText card={card} />}
                </Box>
              </Box>
            </Link>
          )}
        </Box>
      </Box>
    </>
  );
}

export default function CardCard(props) {
  const {
    hover,
    leftClick,
    rightClick,
    buttons,
    dense,
    superDense,
    error,
    card,
  } = props;
  const classes = useStyles();

  if (!card) {
    return <></>;
  }

  if (hover) {
    return (
      <MUIPopper
        content={
          <Box sx={{ minHeight: "300px", overflow: "hidden" }}>
            <CardImagePreview card={card} large />
          </Box>
        }
      >
        <ReturnComponent
          card={card}
          dense={dense}
          superDense={superDense}
          leftClick={leftClick}
          rightClick={rightClick}
          buttons={buttons}
          hover={hover}
          error={error}
          classes={classes}
        />
      </MUIPopper>
    );
  }
  return (
    <ReturnComponent
      card={card}
      dense={dense}
      superDense={superDense}
      leftClick={leftClick}
      rightClick={rightClick}
      buttons={buttons}
      hover={hover}
      error={error}
      classes={classes}
    />
  );
}

function CardStackReturnComponent(props) {
  const { image, loading, large, dense, stack, card } = props;
  if (!image?.url || loading) {
    return (
      <Box
        sx={{
          flex: `0 0 ${large ? "286px" : dense ? "18px" : "214px"}`,
          maxWidth: large ? "286px" : dense ? "18px" : "214px",
          minWidth: large ? "286px" : dense ? "18px" : "214px",
          height: large ? "400px" : dense ? "24px" : "300px",
          mt: "-5px !important",
          borderRadius: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {loading && <CircularProgress size={10} />}
      </Box>
    );
  }
  return (
    <>
      <Box
        sx={{
          display: "inline-block",
          position: "relative",
          height: large
            ? `${400 + (stack.length - 1) * 25}px`
            : `${210 + (stack.length - 1) * 25}px`,
        }}
      >
        {stack.length > 0 &&
          stack.map((i) => (
            <img
              key={i}
              src={image.url}
              alt={card.name}
              style={{
                width: large ? "286px" : "150px",
                mt: "-5px !important",
                borderRadius: 2,

                position: i > 0 ? "absolute" : null,
                left: i > 0 ? "0px" : null,
                top: i > 0 ? `${i * 25}px` : null,
              }}
            />
          ))}
      </Box>
    </>
  );
}

export function CardStack(props) {
  const { hover, card, dense, large } = props;

  var imageCacheState = useContext(ImageCacheContext);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  const [stack, setStack] = useState(null);

  useEffect(() => {
    const go = async () => {
      setLoading(true);
      let aStack = [];
      for (let i = 0; i < card.qty; i++) {
        aStack.push(i);
      }
      setStack(aStack);
      if (!card.imageFileId) {
        setImage({
          url: `${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/assets/cardback.png`,
        });
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === card.imageFileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = card.imageFile?.id
          ? card.imageFile
          : card.image?.id
            ? card.image
            : await imageCacheState.fetchImage(card.imageFileId, "medium");
        if (file) {
          if (!file.url && file.cloudFrontDomain) {
            file.url = `${file.cloudFrontDomain}${
              large
                ? file.sizes?.large || file.filename
                : file.sizes?.medium || file.filename
            }`;
          }

          setImage({
            id: card.imageFileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }

      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [card]);

  if (!card) {
    return <></>;
  }

  if (hover) {
    return (
      <MUIPopper
        content={
          <Box sx={{ minHeight: "300px", overflow: "hidden" }}>
            <CardImagePreview card={card} large />
          </Box>
        }
      >
        <CardStackReturnComponent
          image={image}
          loading={loading}
          large={large}
          dense={dense}
          stack={stack}
          card={card}
        />
      </MUIPopper>
    );
  }

  return (
    <CardStackReturnComponent
      image={image}
      loading={loading}
      large={large}
      dense={dense}
      stack={stack}
      card={card}
    />
  );
}
